import { useEffect, useRef } from 'react';

import {
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'jotai';
import {
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  buildRegisterRequest,
  cleanUpRegistrationData,
  cookieJar,
  uiRoutes,
  useRegisterV2,
} from '@consumer-frontend/nc-api';
import {
  isDefined,
  MaritalInformationFormT,
  maritalStatusOptions,
  MaritalStatusValueT,
  marriedStatuses,
} from '@consumer-frontend/nc-utils';
import { Container, Fieldset } from '@consumer-frontend/shared-ui';

import { NcMaritalInformationSpouseForm } from './NcMaritalInformationSpouseForm';
import { useApplyRouting } from '../../routing';
import {
  accountIdAtom,
  hasVerifiedIdentityAtom,
  isReturningCustomerFlowAtom,
  registrationDataAtom,
  unableToContinueReasonAtom,
} from '../../store/atoms';
import { NcButton } from '../NcButton';
import { NcForm } from '../NcForm';
import { NcPageWithAlertBanner } from '../NcPageWithAlertBanner';
import { NcProgressBar } from '../NcProgressBar';
import { NcSelect } from '../NcSelect';

import '@consumer-bootstrap/brands/netcredit/apply/marital-information-page.scss';

export const NcMaritalInformationPage = () => {
  const hasVerifiedIdentity = useAtomValue(hasVerifiedIdentityAtom);
  const [registrationData, setRegistrationData] = useAtom(registrationDataAtom);
  const refreshAccountId = useSetAtom(accountIdAtom);
  const setUnableToContinueReason = useSetAtom(unableToContinueReasonAtom);
  const isReturningCustomer = useAtomValue(isReturningCustomerFlowAtom);
  const {
    handleBack,
    handleContinue,
    progress,
  } = useApplyRouting();
  const navigate = useNavigate();

  const {
    error,
    registerV2: registerAccount,
  } = useRegisterV2();

  // Navigate to unable-to-continue page on 409 or throw error to trigger the error boundary.
  useEffect(() => {
    if (!isDefined(error)) { return; }

    if (error.response?.status === 409) {
      setUnableToContinueReason('unableToVerify');
      navigate(uiRoutes.apply.unableToContinue);

      return;
    }

    setRegistrationData({});
    throw error;
  }, [
    error,
    navigate,
    setRegistrationData,
    setUnableToContinueReason,
  ]);

  const methods = useForm<MaritalInformationFormT>({
    defaultValues: registrationData.maritalInformation,
    shouldUnregister: true,
  });

  const {
    register,
    handleSubmit,
    resetField,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = methods;

  const maritalStatusValidations = {
    required: 'Select a marital status.',
    validate: (option: MaritalStatusValueT) => (
      maritalStatusOptions.some((maritalStatus) => maritalStatus.value === option)
      || 'Select a marital status.'
    ),
  };

  const selectedMaritalStatus = watch('maritalStatus');
  const showSpouseForm = marriedStatuses.includes(selectedMaritalStatus);
  const previousMaritalStatusRef = useRef<MaritalStatusValueT | undefined>(undefined);

  // Reset spouse fields only when transitioning between two married statuses (i.e., the spouse
  // form remains mounted). Transitions away from married to single/divorced are handled by
  // shouldUnregister: true — calling resetField on already-unregistered fields re-inserts them
  // into the form state with undefined values, which causes spouse.sameAddress validation to fail
  // and silently blocks form submission.
  useEffect(() => {
    if (
      isDefined(previousMaritalStatusRef.current)
      && previousMaritalStatusRef.current !== selectedMaritalStatus
      && showSpouseForm
    ) {
      resetField('spouse.firstName', { defaultValue: '' });
      resetField('spouse.lastName', { defaultValue: '' });
      resetField('spouse.sameAddress', { defaultValue: undefined });
      resetField('spouse.address.addressLine1', { defaultValue: '' });
      resetField('spouse.address.addressLine2', { defaultValue: '' });
      resetField('spouse.address.city', { defaultValue: '' });
      resetField('spouse.address.state', { defaultValue: '' });
      resetField('spouse.address.postalCode', { defaultValue: '' });
    }

    previousMaritalStatusRef.current = selectedMaritalStatus;
  }, [
    selectedMaritalStatus,
    resetField,
    showSpouseForm,
  ]);

  const handleMaritalInformationSubmit: SubmitHandler<MaritalInformationFormT> = async (data) => {
    const trimmedData: MaritalInformationFormT = data.spouse
      ? {
        ...data,
        spouse: {
          ...data.spouse,
          address: data.spouse.sameAddress
            ? registrationData.address
            : data.spouse.address && {
              addressLine1: data.spouse.address.addressLine1.trim(),
              addressLine2: data.spouse.address.addressLine2?.trim(),
              city: data.spouse.address.city.trim(),
              postalCode: data.spouse.address.postalCode.trim(),
              state: data.spouse.address.state,
            },
          firstName: data.spouse.firstName.trim(),
          lastName: data.spouse.lastName.trim(),
        },
      }
      : data;

    // If the user's identity is verified, register the account here
    if (hasVerifiedIdentity) {
      const updatedRegistrationData = { ...registrationData, maritalInformation: trimmedData };
      const requestData = buildRegisterRequest({
        registrationData: updatedRegistrationData,
      });

      const response = await registerAccount(requestData);

      if (!response) {
        return;
      }

      // Save tokens for authenticated endpoints
      cookieJar.saveTokens(response.accessToken, response.refreshToken);
      refreshAccountId();

      await handleContinue({ maritalInformation: trimmedData });

      setRegistrationData(cleanUpRegistrationData(updatedRegistrationData));
    } else {
      await handleContinue({ maritalInformation: trimmedData });
    }
  };

  return (
    <NcPageWithAlertBanner>
      <Container hasBoxShadow className="nc-marital-information">
        <NcProgressBar progress={progress} />

        <h1 className="h3">{isReturningCustomer ? 'Verify Marital Information' : 'Marital Information'}</h1>

        <p className="subheading">
          Wisconsin law requires that we collect some additional information about your marital status.
        </p>

        <FormProvider {...methods}>
          <NcForm
            className="nc-apply-marital-information"
            onSubmit={handleSubmit(handleMaritalInformationSubmit)}
          >
            <NcSelect
              {...register('maritalStatus', maritalStatusValidations)}
              errorMessage={errors.maritalStatus && 'Select a marital status.'}
              id="marital-status"
              label="Marital Status"
              options={maritalStatusOptions}
              placeholder="Select marital status"
            />

            {showSpouseForm && (
              <NcMaritalInformationSpouseForm
                errors={errors}
                register={register}
                setValue={setValue}
                watch={watch}
              />
            )}

            <Fieldset className="cta-buttons">
              <NcButton
                loading={isSubmitting}
                type="submit"
                variant="primary"
              >
                Continue
              </NcButton>

              <NcButton
                disabled={isSubmitting}
                onClick={handleBack}
                type="button"
                variant="outline-primary"
              >
                Back
              </NcButton>
            </Fieldset>
          </NcForm>
        </FormProvider>
      </Container>
    </NcPageWithAlertBanner>
  );
};
